/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/
import type { FetchGraphQL, setFetchGraphQlHeader, removeFetchGraphQlHeader } from '@adobe-commerce/fetch-graphql';
import { config } from '@/company-switcher/api';

/**
 * Manages company-specific headers for GraphQL requests.
 * Singleton class that handles setting and removing company headers across multiple GraphQL modules.
 */
class CompanyHeaderManager {
  private companyHeaderSet: boolean = false;
  private companyHeaderAppliers: typeof setFetchGraphQlHeader[] = [];
  private companyHeaderRemovers: typeof removeFetchGraphQlHeader[] = [];
  private headerKey: string;

  constructor() {
    this.headerKey = config.getConfig().companyHeader;
    this.setFetchGraphQlModules(config.getConfig().fetchGraphQlModules);
  }

  /**
   * Sets the header key used for company identification
   * @param headerKey - The header name to use for company ID
   */
  public setHeaderKey(headerKey: string): void {
    this.headerKey = headerKey;
  }

  /**
   * Configures GraphQL modules that will have company headers applied
   * @param modules - Array of GraphQL modules with header management functions
   */
  public setFetchGraphQlModules(modules: FetchGraphQL[]): void {
    this.companyHeaderAppliers = [];
    this.companyHeaderRemovers = [];
    modules.forEach((module: FetchGraphQL) => {
      this.companyHeaderAppliers.push((key: string, value: string) => 
        module.setFetchGraphQlHeader(key, value)
      );
      this.companyHeaderRemovers.push((key: string) => 
        module.removeFetchGraphQlHeader(key)
      );
    });
  }

  /**
   * Sets company headers for all configured GraphQL modules
   * @param companyId - The company ID to set in headers, or null to remove headers
   */
  public setCompanyHeaders(companyId: string | null): void {
    if (companyId === null) {
      this.removeCompanyHeaders();
      return;
    }
    this.companyHeaderAppliers.forEach((setFn) => {
      setFn(this.headerKey, companyId);
    });
    this.companyHeaderSet = true;
  }

  /**
   * Removes company headers from all configured GraphQL modules
   */
  public removeCompanyHeaders(): void {
    this.companyHeaderRemovers.forEach((removeFn) => {
      removeFn(this.headerKey);
    });
    this.companyHeaderSet = false;
  }

  /**
   * Checks if company headers are currently set
   * @returns true if company headers are set, false otherwise
   */
  public isCompanyHeaderSet(): boolean {
    return this.companyHeaderSet;
  }
}

let _companyHeaderManager: CompanyHeaderManager | null = null;

/**
 * Gets the singleton instance of CompanyHeaderManager
 * @returns The CompanyHeaderManager instance
 */
export const getCompanyHeaderManager = () => {
  if (!_companyHeaderManager) {
    _companyHeaderManager = new CompanyHeaderManager();
  }
  return _companyHeaderManager;
};
